import { NgModule } from '@angular/core';
import {
  AnonymousConsentsModule, AuthModule,
  CostCenterOccModule, ExternalRoutesModule, FeatureToggles, ProductModule, ProductOccModule, provideFeatureTogglesFactory, UserModule, UserOccModule
} from "@spartacus/core";
import {
  AnonymousConsentManagementBannerModule,
  AnonymousConsentsDialogModule,
  BannerCarouselModule,
  BannerModule,
  BreadcrumbModule,
  CategoryNavigationModule,
  CmsParagraphModule,
  ConsentManagementModule,
  HamburgerMenuModule,
  HomePageEventModule,
  LinkModule,
  LoginRouteModule,
  LogoutModule,
  MyCouponsModule,
  MyInterestsModule,
  NavigationEventModule,
  NotificationPreferenceModule,
  PageTitleModule,
  PaymentMethodsModule,
  ProductCarouselModule,
  ProductFacetNavigationModule,
  ProductImagesModule,
  ProductIntroModule,
  ProductListingPageModule,
  ProductListModule,
  ProductPageEventModule,
  ProductReferencesModule,
  ProductTabsModule,
  SiteContextSelectorModule,
  StockNotificationModule,
  TabParagraphContainerModule,
} from "@spartacus/storefront";
import { UserFeatureModule } from './features/user/user-feature.module';
import { TagManagementFeatureModule } from './features/tracking/tag-management-feature.module';
import { ProductConfiguratorFeatureModule } from './features/product-configurator/product-configurator-feature.module';
import { SmartEditFeatureModule } from './features/smartedit/smart-edit-feature.module';
import { AsmFeatureModule } from './features/asm/asm-feature.module';
import { AddressBookModule } from '@spartacus/user/profile/components';
import { CartTotalsModule } from '@spartacus/cart/base/components';
import { CartBaseFeatureModule } from './features/cart/cart-base-feature.module';
import { CartImportExportFeatureModule } from './features/cart/cart-import-export-feature.module';
import { QuickOrderFeatureModule } from './features/cart/cart-quick-order-feature.module';
import { CheckoutFeatureModule } from './features/checkout/checkout-feature.module';
import { OrderFeatureModule } from './features/order/order-feature.module';

@NgModule({
  declarations: [],
  imports: [
    AuthModule.forRoot(),
    LoginRouteModule,
    LogoutModule,
    HamburgerMenuModule,
    SiteContextSelectorModule,
    LinkModule,
    BannerModule,
    CmsParagraphModule,
    TabParagraphContainerModule,
    BannerCarouselModule,
    CategoryNavigationModule,
    BreadcrumbModule,
    PageTitleModule,
    UserModule,
    UserOccModule,
    AddressBookModule,
    PaymentMethodsModule,
    NotificationPreferenceModule,
    MyInterestsModule,
    StockNotificationModule,
    ConsentManagementModule,
    MyCouponsModule,
    AnonymousConsentsModule.forRoot(),
    AnonymousConsentsDialogModule,
    AnonymousConsentManagementBannerModule,
    ProductModule.forRoot(),
    ProductOccModule,
    ProductListingPageModule,
    ProductListModule,
    ProductFacetNavigationModule,
    ProductTabsModule,
    ProductCarouselModule,
    ProductReferencesModule,
    ProductImagesModule,
    ProductIntroModule,
    CostCenterOccModule,
    NavigationEventModule,
    HomePageEventModule,
    ProductPageEventModule,
    ExternalRoutesModule.forRoot(),
    UserFeatureModule,
    CartBaseFeatureModule,
    QuickOrderFeatureModule,
    CartImportExportFeatureModule,
    OrderFeatureModule,
    CheckoutFeatureModule,
    CartTotalsModule,
    TagManagementFeatureModule,
    ProductConfiguratorFeatureModule,
    SmartEditFeatureModule,
    AsmFeatureModule,
  ],
  providers: [
    provideFeatureTogglesFactory(() => {
      const appFeatureToggles: Required<FeatureToggles> = {
        a11yImprovedErrorMessage: false,
        a11yOptimizedMenuSpacing: false,
        a11yWideScreenImprovements: false,
        opfEnablePreventingFromCheckoutWithoutEmail: false,
        a11ySelectImprovementsCustomerTicketingCreateSelectbox: false,
        a11yNgSelectLayering: false,
        a11yUseProperTextColorForFutureStockAccordion: false,
        a11yPopoverHighContrast: false,
        a11yTabsManualActivation: false,
        a11yAnonymousConsentMessageInDialog: false,
        a11yQuickOrderSearchListKeyboardNavigation: false,
        a11yKeyboardAccessibleZoom: false,
        a11yTruncatedTextUnitLevelOrderHistory: false,
        a11yPreventCartItemsFormRedundantRecreation: false,
        a11yResetFocusAfterNavigating: false,
        a11yStoreFinderLabel: false,
        a11yLinkBtnsToTertiaryBtns: false,
        a11yNgSelectAriaLabelDropdownCustomized: false,
        a11yMiniCartFocusOnMobile: false,
        updateConsentGivenInOnChanges: false,
        a11yQuickOrderSearchBoxRefocusOnClose: false,
        a11yKeyboardFocusInSearchBox: false,
        a11yAddPaddingToCarouselPanel: false,
        a11yNavigationButtonsAriaFixes: false,
        a11yFocusOnCardAfterSelecting: false,
        a11ySearchableDropdownFirstElementFocus: false,
        a11yHideConsentButtonWhenBannerVisible: false,
        a11yRepeatingButtonsUniqueLabels: false,
        a11yHighContrastBorders: false,
        a11yRegionAssociatedHeaders: false,
        a11yHamburgerMenuTrapFocus: false,
        a11yScrollToTopPositioning: false,
        enableCarouselCategoryProducts: false,
        enableClaimCustomerCouponWithCodeInRequestBody: false,
        a11yNgSelectAriaControls: false,
        enableReadDomainValuesOnDemand: false,
        readMoreDirective: false,
        productReviewCharactersLeft: false,
        a11yConfiguratorOverviewHeaderVPC: false,
        storeFinderFacadeCleanup: false,
        defaultProductPageRouteAllowsNoProductName: false,
        consistentSizeProductCards: false,
        reserveHorizontalSpaceStarRating: false,
        topProgressBarUseTransformAnimation: false,
        disableCxPageSlotMarginAnimation: false,
        productCarouselScrolling: false,
        cdsLoginEventsToken: true,
        createMediaPreconnectLink: false,
        authorizationCodeFlowByDefault: true,
        unifiedDefaultHeaderSlotsAcrossBreakpoints: false,
        reserveSpaceForImagesOnPdpAndPlp: false,
        lazyLoadImagesByDefault: false,
        incrementProcessesCountForMergeCart: true,
        dispatchLoginActionOnlyWhenTokenReceived: true,
        defaultLayoutConfigWithoutPageFold: false,
        navigationMenuCloseOnSameLinkClick: false,
        enablePasswordExpiredErrorTranslation: false,
        enableQuotePurchaseOrderNumber: false,
        enableReturnOrderReturnableQuantityConsigmentFallback: false,
      };
      return appFeatureToggles;
    }),
  ]
})
export class SpartacusFeaturesModule { }
